<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, vue/max-attributes-per-line, vue/html-closing-bracket-newline, vue/multiline-html-element-content-newline */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import LemitAPI from 'dashboard/api/lemit';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import { format, subDays } from 'date-fns';

const summary = ref({});
const consultations = ref([]);
const batches = ref([]);
const users = ref([]);
const cpf = ref('');
const batchText = ref('');
const selectedFileName = ref('');
const dateRange = ref([subDays(new Date(), 29), new Date()]);
const rangeType = ref(DATE_RANGE_TYPES.LAST_30_DAYS);
const from = ref(format(dateRange.value[0], 'yyyy-MM-dd'));
const to = ref(format(dateRange.value[1], 'yyyy-MM-dd'));
const loading = ref(false);
const consulting = ref(false);
const consultingCpf = ref('');
const processingBatch = ref(false);
const downloadingBatchId = ref('');
const creatingPhoneKey = ref('');
const savingAccesses = ref(false);
const accessDialogRef = ref(null);
const campaignDialogRef = ref(null);
const relatedConsultDialogRef = ref(null);
const agentSearch = ref('');
const enabledIds = ref([]);
const draftEnabledIds = ref([]);
const route = useRoute();
const router = useRouter();
const result = ref(null);
const cachedConsultations = ref([]);
const activeMode = ref('single');
const providers = [
  {
    id: 'lemit',
    name: 'Lemit',
    description:
      'Consulta Plus com enriquecimento cadastral e verificação de WhatsApp.',
    icon: 'i-lucide-user-round-search',
    eyebrow: 'Consulta Plus',
    accent: 'violet',
    features: [
      'Consulta Plus',
      'Verificação de WhatsApp',
      'Dados cadastrais e contatos',
    ],
  },
  {
    id: 'nova_era',
    name: 'Nova Era',
    description: 'Dados cadastrais e celulares com WhatsApp.',
    icon: 'i-lucide-smartphone',
    eyebrow: 'WhatsApp localizado',
    accent: 'blue',
    features: ['Celulares com WhatsApp', 'Dados cadastrais', 'Score e perfil'],
  },
];
const selectedProvider = ref(
  ['lemit', 'nova_era'].includes(route?.query?.produto)
    ? route.query.produto
    : ''
);
const selectedCampaignBatch = ref(null);
const selectedRelation = ref(null);
const campaignPhonePriority = ref('1');
const preparingCampaign = ref(false);
const INITIAL_BATCH_REFRESH_DELAY = 5000;
const MAX_BATCH_REFRESH_DELAY = 60000;
let batchRefreshTimer;
let batchRefreshDelay = INITIAL_BATCH_REFRESH_DELAY;
let loadSequence = 0;

const resultCacheKey = computed(
  () =>
    `talkvia:hygiene:${selectedProvider.value}:consultations:${route.params.accountId}`
);
const campaignDraftKey = computed(
  () => `talkvia:lemit:campaign-draft:${route.params.accountId}`
);
const campaignPhoneOptions = [
  {
    value: '1',
    title: 'Telefone principal',
    description: 'Usa a primeira e mais relevante opção de cada CPF.',
  },
  {
    value: '2',
    title: 'Segunda opção',
    description: 'Usa o segundo telefone disponível de cada CPF.',
  },
  {
    value: '3',
    title: 'Terceira opção',
    description: 'Usa o terceiro telefone disponível de cada CPF.',
  },
  {
    value: 'all',
    title: 'Todos os telefones',
    description:
      'Inclui todas as opções localizadas, removendo números repetidos.',
  },
];

const providerName = computed(
  () =>
    providers.find(item => item.id === selectedProvider.value)?.name || 'Lemit'
);

const isAdmin = computed(() => summary.value.administrator);
const balance = computed(() => Number(summary.value.balance || 0));
const providerAvailable = computed(() =>
  Boolean(summary.value.configured && summary.value.product)
);
const creditCost = computed(() =>
  Number(summary.value.product?.credits_per_use || 1)
);
const hasBalance = computed(() => balance.value >= creditCost.value);
const normalizeCpf = value => {
  const digits = String(value || '').replace(/\D/g, '');
  return digits.length > 0 && digits.length <= 11
    ? digits.padStart(11, '0')
    : digits;
};
const isValidCpf = value => {
  const digits = normalizeCpf(value);
  if (!/^\d{11}$/.test(digits) || /^(\d)\1{10}$/.test(digits)) return false;

  const calculateDigit = length => {
    const sum = digits
      .slice(0, length)
      .split('')
      .reduce(
        (total, digit, index) => total + Number(digit) * (length + 1 - index),
        0
      );
    const remainder = sum % 11;
    return remainder < 2 ? 0 : 11 - remainder;
  };

  return (
    Number(digits[9]) === calculateDigit(9) &&
    Number(digits[10]) === calculateDigit(10)
  );
};
const canConsultCpf = computed(
  () => providerAvailable.value && isValidCpf(cpf.value)
);
const person = computed(() => result.value?.pessoa || {});
const creditRisk = computed(() => result.value?.risco_credito || {});
const creditRiskScore = value =>
  String(value?.score_credito || '')
    .trim()
    .toUpperCase() || '-';
const creditRiskClass = value => {
  const score = creditRiskScore(value);
  if (['BAIXO', 'MUITO BAIXO'].includes(score)) {
    return 'bg-n-teal-3 text-n-teal-11';
  }
  if (['MÉDIO', 'MEDIO', 'MODERADO'].includes(score)) {
    return 'bg-n-amber-3 text-n-amber-11';
  }
  if (['ALTO', 'MUITO ALTO'].includes(score)) {
    return 'bg-n-ruby-3 text-n-ruby-11';
  }
  return 'bg-n-alpha-2 text-n-slate-11';
};
const phoneKey = phone =>
  `${phone?.ddd || ''}${phone?.numero || ''}`.replace(/\D/g, '');
const phoneContact = phone =>
  result.value?.phone_contacts?.[phoneKey(phone)] || {};
const batchCpfs = computed(() => {
  return [
    ...new Set(
      batchText.value
        .split(/[\s,;]+/)
        .map(normalizeCpf)
        .filter(isValidCpf)
    ),
  ];
});
const batchCost = computed(() => batchCpfs.value.length * creditCost.value);
const canProcessBatch = computed(
  () =>
    batchCpfs.value.length > 0 &&
    batchCpfs.value.length <= 2000 &&
    providerAvailable.value &&
    balance.value >= batchCost.value
);
const filteredUsers = computed(() => {
  const query = agentSearch.value.trim().toLocaleLowerCase('pt-BR');
  if (!query) return users.value;
  return users.value.filter(user =>
    `${user.name} ${user.email} ${user.role}`
      .toLocaleLowerCase('pt-BR')
      .includes(query)
  );
});
const authorizedUsersCount = computed(
  () =>
    users.value.filter(
      user => user.automatic_access || enabledIds.value.includes(user.id)
    ).length
);
const draftAuthorizedUsersCount = computed(
  () =>
    users.value.filter(
      user => user.automatic_access || draftEnabledIds.value.includes(user.id)
    ).length
);

const load = async () => {
  loadSequence += 1;
  const sequence = loadSequence;
  const requestedProvider = selectedProvider.value;
  loading.value = true;
  try {
    const [{ data: info }, { data: logs }] = await Promise.all([
      LemitAPI.summary(requestedProvider),
      LemitAPI.logs({ from: from.value, to: to.value }, requestedProvider),
    ]);
    if (requestedProvider !== selectedProvider.value) return;
    summary.value = info;
    consultations.value = logs.consultations || [];
    batches.value = logs.batches || [];
    if (info.administrator) {
      const { data } = await LemitAPI.accesses();
      users.value = data.users || [];
      enabledIds.value = users.value
        .filter(user => user.enabled && !user.automatic_access)
        .map(user => user.id);
    }
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        `Não foi possível carregar a consulta ${providerName.value}.`
    );
  } finally {
    if (sequence === loadSequence) loading.value = false;
  }
};

const stopBatchRefresh = () => {
  window.clearTimeout(batchRefreshTimer);
  batchRefreshTimer = undefined;
};

const scheduleBatchRefresh = () => {
  stopBatchRefresh();
  if (document.hidden || !batches.value.some(batch => !batch.complete)) {
    return;
  }

  batchRefreshTimer = window.setTimeout(async () => {
    try {
      const { data } = await LemitAPI.logs(
        { from: from.value, to: to.value },
        selectedProvider.value
      );
      consultations.value = data.consultations || [];
      batches.value = data.batches || [];
      batchRefreshDelay = INITIAL_BATCH_REFRESH_DELAY;
    } catch (error) {
      if ([401, 403].includes(error.response?.status)) {
        stopBatchRefresh();
        return;
      }

      batchRefreshDelay = Math.min(
        batchRefreshDelay * 2,
        MAX_BATCH_REFRESH_DELAY
      );
    }

    scheduleBatchRefresh();
  }, batchRefreshDelay);
};

const handleVisibilityChange = () => {
  if (document.hidden) {
    stopBatchRefresh();
  } else {
    scheduleBatchRefresh();
  }
};

const loadConsultationCache = () => {
  try {
    const cached = JSON.parse(
      localStorage.getItem(resultCacheKey.value) || '[]'
    );
    cachedConsultations.value = Array.isArray(cached)
      ? cached.slice(0, 50)
      : [];
  } catch {
    cachedConsultations.value = [];
  }
};

const saveConsultationCache = () => {
  const items = cachedConsultations.value.slice(0, 50);
  while (items.length) {
    try {
      localStorage.setItem(resultCacheKey.value, JSON.stringify(items));
      cachedConsultations.value = items;
      return;
    } catch {
      items.pop();
    }
  }
  useAlert('O navegador não conseguiu salvar o histórico local.');
};

const cacheConsultation = consultation => {
  const cached = {
    ...consultation,
    cached_at: consultation.cached_at || new Date().toISOString(),
  };
  cachedConsultations.value = [
    cached,
    ...cachedConsultations.value.filter(item => item.id !== cached.id),
  ].slice(0, 50);
  saveConsultationCache();
};

const performConsult = async cpfValue => {
  const normalizedCpf = normalizeCpf(cpfValue);
  if (
    !providerAvailable.value ||
    !hasBalance.value ||
    !isValidCpf(normalizedCpf)
  )
    return;

  consulting.value = true;
  consultingCpf.value = normalizedCpf;
  try {
    const { data } = await LemitAPI.consult(
      normalizedCpf,
      selectedProvider.value
    );
    result.value = data;
    cacheConsultation(data);
    cpf.value = '';
    useAlert('Consulta concluída.', { variant: 'positive' });
    await load();
  } catch (error) {
    useAlert(error.response?.data?.error || 'Consulta não realizada.');
  } finally {
    consulting.value = false;
    consultingCpf.value = '';
  }
};

const consult = () => performConsult(cpf.value);

const requestRelatedCpfConsult = relation => {
  if (!providerAvailable.value) {
    useAlert(`${providerName.value} ainda não está disponível.`);
    return;
  }
  if (!hasBalance.value) {
    useAlert('Saldo insuficiente para consultar este vínculo.');
    return;
  }
  selectedRelation.value = relation;
  relatedConsultDialogRef.value?.open();
};

const confirmRelatedCpfConsult = async () => {
  if (!selectedRelation.value) return;

  await performConsult(selectedRelation.value.cpf_vinculo);
  relatedConsultDialogRef.value?.close();
};

const clearSelectedRelation = () => {
  selectedRelation.value = null;
};

const updateCachedConsultation = consultation => {
  cachedConsultations.value = cachedConsultations.value.map(item =>
    item.id === consultation.id
      ? { ...consultation, cached_at: item.cached_at }
      : item
  );
  saveConsultationCache();
};

const showCachedConsultation = consultation => {
  result.value = JSON.parse(JSON.stringify(consultation));
  window.requestAnimationFrame(() =>
    document.querySelector('#lemit-result')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
  );
};

const clearConsultationCache = () => {
  cachedConsultations.value = [];
  localStorage.removeItem(resultCacheKey.value);
};

const copyPhone = async phone => {
  const number = `+55${phoneKey(phone)}`;
  try {
    await navigator.clipboard.writeText(number);
    useAlert(`${number} copiado.`, { variant: 'positive' });
  } catch {
    useAlert('Não foi possível copiar o celular.');
  }
};

const openContactDestination = contact => {
  if (contact.conversation_id) {
    router.push({
      name: 'inbox_conversation',
      params: {
        accountId: route.params.accountId,
        conversation_id: contact.conversation_id,
      },
    });
    return;
  }
  router.push({
    name: 'contacts_edit',
    params: {
      accountId: route.params.accountId,
      contactId: contact.contact_id,
    },
  });
};

const handlePhoneContact = async phone => {
  const key = phoneKey(phone);
  const existing = phoneContact(phone);
  if (existing.contact_id) {
    openContactDestination(existing);
    return;
  }

  creatingPhoneKey.value = key;
  try {
    const { data } = await LemitAPI.createPhoneContact(
      result.value.id,
      key,
      selectedProvider.value
    );
    result.value = {
      ...result.value,
      phone_contacts: { ...result.value.phone_contacts, [key]: data },
    };
    updateCachedConsultation(result.value);
    useAlert('Contato criado com sucesso.', { variant: 'positive' });
    openContactDestination(data);
  } catch (error) {
    useAlert(
      error.response?.data?.error || 'Não foi possível criar o contato.'
    );
  } finally {
    creatingPhoneKey.value = '';
  }
};

const phoneActionLabel = phone => {
  const contact = phoneContact(phone);
  if (contact.conversation_id) return 'Abrir conversa';
  if (contact.contact_id) return 'Abrir contato';
  return 'Criar contato';
};
const phonePriorityLabel = value => {
  const priority = Number(value);
  if (priority === 1) return 'Telefone principal';
  if (priority === 2) return 'Segunda opção';
  if (priority === 3) return 'Terceira opção';
  return priority > 0 ? `${priority}ª opção` : 'Telefone localizado';
};
const addressPriorityLabel = value => {
  const priority = Number(value);
  if (priority === 1) return 'Endereço principal';
  if (priority === 2) return 'Segundo endereço';
  if (priority === 3) return 'Terceiro endereço';
  return priority > 0 ? `${priority}º endereço` : '';
};

const formatDate = value =>
  value ? new Date(value).toLocaleString('pt-BR') : '-';
const formatBirthDate = value =>
  value
    ? new Date(value).toLocaleDateString('pt-BR', { timeZone: 'UTC' })
    : '-';
const formatCpf = value =>
  String(value || '').replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
const formatPhone = phone => (phone ? `(${phone.ddd}) ${phone.numero}` : '-');
const genderLabel = value =>
  ({ M: 'Masculino', F: 'Feminino' })[String(value || '').toUpperCase()] ||
  value ||
  '-';
const deceasedLabel = value => {
  if (value === true) return 'Sim';
  if (value === false) return 'Não';
  return '-';
};
const formatAddress = address =>
  [
    address?.logradouro || address?.endereco,
    address?.numero,
    address?.complemento,
    address?.bairro,
    address?.cidade,
    address?.uf,
    address?.cep,
  ]
    .filter(Boolean)
    .join(', ');
const openAddressOnMaps = address => {
  const query = formatAddress(address);
  if (!query) return;

  window.open(
    `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`,
    '_blank',
    'noopener,noreferrer'
  );
};

const escapeHtml = value =>
  String(value ?? '-')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

const downloadPdf = () => {
  const report = window.open('', '_blank');
  if (!report) {
    useAlert('Permita pop-ups para gerar o PDF da consulta.');
    return;
  }
  report.opener = null;

  const phones = (person.value.celulares || [])
    .map(
      phone =>
        `<li>${escapeHtml(formatPhone(phone))}${phone.whatsapp ? ' · WhatsApp' : ''}${phone.plus ? ' · Plus' : ''}</li>`
    )
    .join('');
  const addresses = (person.value.enderecos || [])
    .map(address => `<li>${escapeHtml(formatAddress(address))}</li>`)
    .join('');
  const relations = (person.value.vinculos || [])
    .map(
      relation =>
        `<li>${escapeHtml(relation.nome_vinculo)} · ${escapeHtml(relation.tipo_vinculo)} · ${escapeHtml(formatCpf(relation.cpf_vinculo))}</li>`
    )
    .join('');

  report.document.write(
    `<!doctype html><html><head><meta charset="utf-8"><title>consulta-cpf-${escapeHtml(person.value.cpf)}</title><style>body{font-family:Arial,sans-serif;color:#1f2937;margin:36px}h1{font-size:22px;margin:0 0 4px}h2{font-size:15px;margin:24px 0 8px;border-bottom:1px solid #ddd;padding-bottom:6px}.muted{color:#6b7280;font-size:12px}.grid{display:grid;grid-template-columns:1fr 1fr;gap:8px 24px}.item{padding:8px 0}.label{display:block;color:#6b7280;font-size:10px;text-transform:uppercase;margin-bottom:3px}ul{margin:0;padding-left:20px}li{margin:6px 0}@media print{body{margin:18mm}}</style></head><body><h1>${escapeHtml(person.value.nome || 'Consulta de CPF')}</h1><div class="muted">Consulta realizada em ${escapeHtml(formatDate(result.value.created_at))}</div><h2>Dados cadastrais</h2><div class="grid"><div class="item"><span class="label">CPF</span>${escapeHtml(formatCpf(person.value.cpf))}</div><div class="item"><span class="label">Risco de crédito</span>${escapeHtml(creditRiskScore(creditRisk.value))}</div><div class="item"><span class="label">Nascimento</span>${escapeHtml(formatBirthDate(person.value.data_nascimento))}</div><div class="item"><span class="label">Sexo</span>${escapeHtml(genderLabel(person.value.sexo))}</div><div class="item"><span class="label">Falecido</span>${escapeHtml(deceasedLabel(person.value.falecido))}</div><div class="item"><span class="label">Situação do CPF</span>${escapeHtml(person.value.situacao_cpf)}</div><div class="item"><span class="label">Ocupação</span>${escapeHtml(person.value.ocupacao)}</div><div class="item"><span class="label">Nome da mãe</span>${escapeHtml(person.value.nome_mae)}</div></div><h2>Celulares</h2><ul>${phones || '<li>Nenhum celular informado</li>'}</ul><h2>Endereços</h2><ul>${addresses || '<li>Nenhum endereço informado</li>'}</ul><h2>Vínculos</h2><ul>${relations || '<li>Nenhum vínculo informado</li>'}</ul></body></html>`
  );
  report.document.close();
  report.focus();
  window.setTimeout(() => report.print(), 250);
};

const sendBatch = async () => {
  if (!canProcessBatch.value) return;
  processingBatch.value = true;
  try {
    const { data } = await LemitAPI.batch(
      batchText.value,
      selectedProvider.value
    );
    batchText.value = '';
    selectedFileName.value = '';
    useAlert(
      `Lote criado com ${data.count} CPFs. Você pode acompanhar o processamento abaixo.`,
      { variant: 'positive' }
    );
    await load();
  } catch (error) {
    useAlert(error.response?.data?.error || 'Não foi possível criar o lote.');
  } finally {
    processingBatch.value = false;
  }
};

const readFile = async event => {
  const file = event.target.files?.[0];
  selectedFileName.value = file?.name || '';
  batchText.value = (await file?.text()) || '';
};

const downloadBatch = async batch => {
  downloadingBatchId.value = batch.id;
  try {
    const response = await LemitAPI.downloadBatch(
      batch.id,
      selectedProvider.value
    );
    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${selectedProvider.value}-lote-${batch.id}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  } catch {
    useAlert('Não foi possível baixar o arquivo deste lote.');
  } finally {
    downloadingBatchId.value = '';
  }
};

const openCampaignDialog = batch => {
  selectedCampaignBatch.value = batch;
  campaignPhonePriority.value = '1';
  campaignDialogRef.value?.open();
};

const prepareBatchCampaign = async () => {
  if (!selectedCampaignBatch.value) return;

  preparingCampaign.value = true;
  try {
    const { data } = await LemitAPI.campaignAudience(
      selectedCampaignBatch.value.id,
      campaignPhonePriority.value,
      selectedProvider.value
    );
    if (!data.numbers?.length) {
      useAlert('Nenhum telefone foi encontrado para a opção selecionada.');
      return;
    }

    sessionStorage.setItem(
      campaignDraftKey.value,
      JSON.stringify({
        title: `Campanha lote ${providerName.value} ${selectedCampaignBatch.value.id.slice(0, 8)}`,
        numbers: data.numbers,
        batchId: data.batch_id,
        phonePriority: data.phone_priority,
      })
    );
    campaignDialogRef.value?.close();
    router.push({
      name: 'campaigns_whatsapp_index',
      params: { accountId: route.params.accountId },
      query: { origem: 'higienizacao' },
    });
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        'Não foi possível preparar os telefones para a campanha.'
    );
  } finally {
    preparingCampaign.value = false;
  }
};

const saveAccesses = async () => {
  savingAccesses.value = true;
  try {
    await LemitAPI.updateAccesses(draftEnabledIds.value);
    enabledIds.value = [...draftEnabledIds.value];
    useAlert('Acessos atualizados.', { variant: 'positive' });
    accessDialogRef.value?.close();
  } catch (error) {
    useAlert(
      error.response?.data?.error || 'Não foi possível salvar os acessos.'
    );
  } finally {
    savingAccesses.value = false;
  }
};

const openAccessDialog = () => {
  agentSearch.value = '';
  draftEnabledIds.value = [...enabledIds.value];
  accessDialogRef.value?.open();
};

const userHasAccess = user =>
  user.automatic_access || draftEnabledIds.value.includes(user.id);

const updateUserAccess = (user, enabled) => {
  if (user.automatic_access) return;
  draftEnabledIds.value = enabled
    ? [...new Set([...draftEnabledIds.value, user.id])]
    : draftEnabledIds.value.filter(id => id !== user.id);
};

const openCreditShop = () => router.push({ name: 'talkvia_shop' });

const onDateRangeChanged = ([startDate, endDate, selectedRange]) => {
  if (!startDate || !endDate) return;

  dateRange.value = [startDate, endDate];
  rangeType.value = selectedRange;
  from.value = format(startDate, 'yyyy-MM-dd');
  to.value = format(endDate, 'yyyy-MM-dd');
  load();
};

const sourceLabel = value => (value === 'batch' ? 'Lote' : 'Individual');
const statusLabel = value =>
  ({ success: 'Concluída', failed: 'Falhou', processing: 'Processando' })[
    value
  ] || value;
const statusClass = value =>
  ({
    success: 'bg-n-teal-3 text-n-teal-11',
    failed: 'bg-n-ruby-3 text-n-ruby-11',
    processing: 'bg-n-amber-3 text-n-amber-11',
  })[value] || 'bg-n-slate-3 text-n-slate-11';
const batchPercent = batch =>
  batch.total
    ? Math.min(100, Math.round((batch.processed / batch.total) * 100))
    : 0;

const selectProvider = provider => {
  if (selectedProvider.value === provider) return;
  selectedProvider.value = provider;
};

const backToProviderSelection = () => {
  selectedProvider.value = '';
};

onMounted(() => {
  if (selectedProvider.value) {
    loadConsultationCache();
    load();
  }
  document.addEventListener('visibilitychange', handleVisibilityChange);
});
watch(batches, scheduleBatchRefresh, { deep: true });
watch(selectedProvider, async provider => {
  stopBatchRefresh();
  result.value = null;
  summary.value = {};
  consultations.value = [];
  batches.value = [];
  batchText.value = '';
  selectedFileName.value = '';
  cachedConsultations.value = [];
  const query = { ...route.query };
  if (provider) query.produto = provider;
  else delete query.produto;
  loading.value = Boolean(provider);
  await router.replace({ query });
  if (!provider) {
    loading.value = false;
    return;
  }
  loadConsultationCache();
  await load();
});
onBeforeUnmount(() => {
  stopBatchRefresh();
  document.removeEventListener('visibilitychange', handleVisibilityChange);
});
</script>

<template>
  <main class="h-full min-h-0 w-full overflow-y-auto">
    <div
      class="mx-auto grid w-full max-w-7xl gap-6 px-4 py-6 pb-24 sm:px-6 lg:px-8"
    >
      <Transition name="hygiene-view" mode="out-in">
        <section
          v-if="!selectedProvider"
          key="provider-selection"
          class="relative isolate overflow-hidden rounded-3xl border border-n-weak bg-n-solid-1 px-5 py-8 shadow-sm sm:px-8 lg:px-10 lg:py-12"
        >
          <div
            class="pointer-events-none absolute -right-24 -top-32 -z-10 size-96 rounded-full bg-n-brand/10 blur-3xl"
          />
          <div
            class="pointer-events-none absolute -bottom-32 -left-24 -z-10 size-80 rounded-full bg-n-violet-5/10 blur-3xl"
          />

          <div class="mx-auto max-w-2xl text-center">
            <span
              class="mx-auto grid size-14 place-items-center rounded-2xl bg-n-brand/10 text-n-brand shadow-sm"
            >
              <i class="i-lucide-sparkles size-7" />
            </span>
            <p
              class="mt-5 text-xs font-semibold uppercase tracking-[0.18em] text-n-brand"
            >
              Higienização cadastral
            </p>
            <h1
              class="mt-2 text-3xl font-semibold tracking-tight text-n-slate-12 sm:text-4xl"
            >
              Como você deseja consultar?
            </h1>
            <p class="mx-auto mt-3 max-w-xl text-sm leading-6 text-n-slate-11">
              Selecione o produto ideal para localizar, validar e enriquecer os
              dados dos seus contatos.
            </p>
          </div>

          <div
            class="mx-auto mt-8 grid max-w-5xl gap-5 md:grid-cols-2 lg:mt-10"
          >
            <button
              v-for="provider in providers"
              :key="provider.id"
              type="button"
              class="provider-card group"
              :class="`provider-card--${provider.accent}`"
              @click="selectProvider(provider.id)"
            >
              <span class="provider-card__glow" />
              <span class="relative flex h-full flex-col">
                <span class="flex items-start justify-between gap-4">
                  <span class="provider-card__icon">
                    <i :class="provider.icon" class="size-7" />
                  </span>
                  <span class="provider-card__arrow">
                    <i class="i-lucide-arrow-up-right size-5" />
                  </span>
                </span>

                <span class="mt-6">
                  <small
                    class="provider-card__eyebrow"
                    :class="`provider-card__eyebrow--${provider.accent}`"
                    >{{ provider.eyebrow }}</small
                  >
                  <strong
                    class="mt-2 block text-2xl font-semibold text-n-slate-12"
                    >{{ provider.name }}</strong
                  >
                  <span class="mt-2 block text-sm leading-6 text-n-slate-11">
                    {{ provider.description }}
                  </span>
                </span>

                <span class="mt-6 grid gap-2 border-t border-n-weak pt-5">
                  <span
                    v-for="feature in provider.features"
                    :key="feature"
                    class="flex items-center gap-2 text-sm text-n-slate-11"
                  >
                    <i class="i-lucide-circle-check size-4 text-n-teal-10" />
                    {{ feature }}
                  </span>
                </span>

                <span
                  class="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-n-slate-12"
                >
                  Acessar {{ provider.name }}
                  <i
                    class="i-lucide-arrow-right size-4 transition-transform group-hover:translate-x-1"
                  />
                </span>
              </span>
            </button>
          </div>

          <p
            class="mx-auto mt-8 flex max-w-xl items-center justify-center gap-2 text-center text-xs text-n-slate-10"
          >
            <i class="i-lucide-shield-check size-4" />Cada produto possui saldo
            e histórico de consultas independentes.
          </p>
        </section>

        <div v-else key="provider-workspace" class="grid gap-6">
          <div class="flex flex-wrap items-center justify-between gap-3">
            <button
              type="button"
              class="inline-flex items-center gap-2 rounded-lg px-1 py-2 text-sm font-medium text-n-slate-11 transition hover:text-n-brand"
              @click="backToProviderSelection"
            >
              <i class="i-lucide-arrow-left size-4" />Voltar para Higienização
            </button>
            <span
              class="inline-flex items-center gap-2 rounded-full border border-n-weak bg-n-solid-1 px-3 py-1.5 text-xs font-semibold text-n-slate-11 shadow-sm"
            >
              <i class="i-lucide-scan-search size-3.5 text-n-brand" />Produto:
              {{ providerName }}
            </span>
          </div>

          <header
            class="relative overflow-hidden rounded-2xl border border-n-weak bg-n-solid-1 p-6 shadow-sm lg:p-8"
          >
            <div
              class="pointer-events-none absolute -right-24 -top-32 size-80 rounded-full bg-n-brand/10 blur-3xl"
            />
            <div
              class="relative flex flex-wrap items-start justify-between gap-6"
            >
              <div class="max-w-2xl">
                <div
                  class="mb-3 inline-flex items-center gap-2 rounded-full bg-n-brand/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-n-brand"
                >
                  <span class="i-lucide-shield-check size-3.5" />Enriquecimento
                  cadastral
                </div>
                <h1
                  class="text-3xl font-semibold tracking-tight text-n-slate-12 lg:text-4xl"
                >
                  Consulta de CPF — {{ providerName }}
                </h1>
                <p class="mt-2 text-base leading-7 text-n-slate-11">
                  Consulte dados cadastrais individualmente ou processe arquivos
                  com até 2.000 CPFs.
                </p>
              </div>
              <div
                class="min-w-64 rounded-xl border border-n-weak bg-n-solid-2 p-4"
              >
                <div class="flex items-center justify-between">
                  <div>
                    <p
                      class="text-xs font-medium uppercase tracking-wide text-n-slate-10"
                    >
                      Saldo disponível
                    </p>
                    <p class="mt-1 text-2xl font-semibold text-n-slate-12">
                      {{ balance.toLocaleString('pt-BR') }}
                      <span class="text-sm font-normal text-n-slate-10"
                        >créditos</span
                      >
                    </p>
                  </div>
                  <span
                    class="grid size-11 place-items-center rounded-xl bg-n-brand/10 text-n-brand"
                    ><i class="i-lucide-coins size-5"
                  /></span>
                </div>
                <Button
                  v-if="balance === 0 && isAdmin"
                  label="Comprar créditos"
                  icon="i-lucide-shopping-cart"
                  class="mt-4 w-full"
                  @click="openCreditShop"
                />
                <p
                  v-else-if="balance === 0"
                  class="mt-3 text-xs text-n-amber-11"
                >
                  Solicite a compra de créditos ao administrador da conta.
                </p>
              </div>
            </div>
          </header>

          <section
            v-if="!loading && (!summary.configured || !summary.product)"
            class="flex items-start gap-3 rounded-xl border border-n-amber-6 bg-n-amber-2 px-4 py-3 text-n-amber-11"
          >
            <i class="i-lucide-triangle-alert mt-0.5 size-5 shrink-0" />
            <div>
              <strong class="text-sm">{{ providerName }} indisponível</strong>
              <p class="mt-0.5 text-sm">
                {{
                  !summary.product
                    ? 'O produto de créditos ainda não foi cadastrado ou está inativo.'
                    : 'As credenciais da integração ainda não foram configuradas.'
                }}
              </p>
            </div>
          </section>

          <section class="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <article class="metric-card">
              <span class="metric-icon bg-n-blue-3 text-n-blue-11"
                ><i class="i-lucide-wallet-cards size-5"
              /></span>
              <div>
                <p>Custo por consulta</p>
                <strong
                  >{{ creditCost }} crédito<span v-if="creditCost !== 1"
                    >s</span
                  ></strong
                >
              </div>
            </article>
            <article class="metric-card">
              <span class="metric-icon bg-n-violet-3 text-n-violet-11"
                ><i class="i-lucide-files size-5"
              /></span>
              <div>
                <p>Limite por lote</p>
                <strong>2.000 CPFs</strong>
              </div>
            </article>
            <article class="metric-card">
              <span class="metric-icon bg-n-teal-3 text-n-teal-11"
                ><i class="i-lucide-circle-check-big size-5"
              /></span>
              <div>
                <p>Consultas exibidas</p>
                <strong>{{ consultations.length }}</strong>
              </div>
            </article>
            <article class="metric-card">
              <span class="metric-icon bg-n-amber-3 text-n-amber-11"
                ><i class="i-lucide-layers-3 size-5"
              /></span>
              <div>
                <p>Lotes recentes</p>
                <strong>{{ batches.length }}</strong>
              </div>
            </article>
          </section>

          <section
            class="rounded-2xl border border-n-weak bg-n-solid-1 shadow-sm"
          >
            <div class="flex border-b border-n-weak bg-n-solid-2/60 p-1.5">
              <button
                class="mode-tab"
                :class="{ 'mode-tab--active': activeMode === 'single' }"
                @click="activeMode = 'single'"
              >
                <i class="i-lucide-user-search size-4" />Consulta individual</button
              ><button
                class="mode-tab"
                :class="{ 'mode-tab--active': activeMode === 'batch' }"
                @click="activeMode = 'batch'"
              >
                <i class="i-lucide-list-checks size-4" />Consulta em lote
              </button>
            </div>

            <div
              v-if="activeMode === 'single'"
              class="grid gap-6 p-5 lg:grid-cols-[minmax(0,1fr)_320px] lg:p-7"
            >
              <div>
                <h2 class="text-xl font-semibold text-n-slate-12">
                  Localizar dados por CPF
                </h2>
                <p class="mt-1 text-sm text-n-slate-11">
                  A consulta consome {{ creditCost }} crédito e pode atualizar
                  um contato correspondente.
                </p>
                <div class="mt-5 flex flex-col items-end gap-3 sm:flex-row">
                  <Input
                    v-model="cpf"
                    class="flex-1"
                    label="CPF"
                    placeholder="000.000.000-00"
                    maxlength="14"
                    @enter="consult"
                  />
                  <Button
                    class="min-w-36"
                    :label="consulting ? 'Consultando…' : 'Consultar CPF'"
                    icon="i-lucide-search"
                    :is-loading="consulting"
                    :disabled="consulting || !hasBalance || !canConsultCpf"
                    @click="consult"
                  />
                </div>
                <p class="mt-2 text-xs text-n-slate-10">
                  Digite com ou sem pontos e hífen. Se o CPF perdeu zeros à
                  esquerda, eles serão adicionados automaticamente.
                </p>
                <div
                  v-if="!hasBalance"
                  class="mt-4 flex items-center gap-2 rounded-lg bg-n-amber-2 px-3 py-2 text-sm text-n-amber-11"
                >
                  <i class="i-lucide-circle-dollar-sign size-4" />Saldo
                  insuficiente para realizar uma nova consulta.
                </div>
              </div>
              <aside class="rounded-xl border border-n-weak bg-n-solid-2 p-4">
                <p
                  class="text-xs font-semibold uppercase tracking-wide text-n-slate-10"
                >
                  O que será retornado
                </p>
                <ul class="mt-3 grid gap-2 text-sm text-n-slate-11">
                  <li class="flex gap-2">
                    <i
                      class="i-lucide-check mt-0.5 size-4 text-n-teal-10"
                    />Dados pessoais e nascimento
                  </li>
                  <li class="flex gap-2">
                    <i
                      class="i-lucide-check mt-0.5 size-4 text-n-teal-10"
                    />Telefones, e-mails e endereços
                  </li>
                  <li class="flex gap-2">
                    <i
                      class="i-lucide-check mt-0.5 size-4 text-n-teal-10"
                    />Atualização do contato localizado
                  </li>
                </ul>
              </aside>
            </div>

            <div
              v-else
              class="grid gap-6 p-5 lg:grid-cols-[minmax(0,1fr)_320px] lg:p-7"
            >
              <div>
                <div class="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h2 class="text-xl font-semibold text-n-slate-12">
                      Processar lista de CPFs
                    </h2>
                    <p class="mt-1 text-sm text-n-slate-11">
                      Cole os CPFs por linha, vírgula ou ponto e vírgula, ou
                      carregue um arquivo TXT/CSV.
                    </p>
                  </div>
                  <span
                    class="rounded-full px-3 py-1 text-xs font-semibold"
                    :class="
                      batchCpfs.length > 2000
                        ? 'bg-n-ruby-3 text-n-ruby-11'
                        : 'bg-n-brand/10 text-n-brand'
                    "
                    >{{ batchCpfs.length.toLocaleString('pt-BR') }} / 2.000
                    CPFs</span
                  >
                </div>
                <TextArea
                  v-model="batchText"
                  class="mt-5"
                  custom-text-area-class="font-mono text-sm leading-6"
                  min-height="13rem"
                  max-height="24rem"
                  auto-height
                  resize
                  placeholder="000.000.000-00&#10;111.111.111-11, 222.222.222-22; 333.333.333-33"
                />
                <div
                  class="mt-3 flex flex-wrap items-center justify-between gap-3"
                >
                  <label
                    class="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-n-strong px-3 py-2 text-sm font-medium text-n-slate-11 transition hover:border-n-brand hover:text-n-brand"
                    ><i class="i-lucide-upload size-4" /><span>{{
                      selectedFileName || 'Selecionar TXT ou CSV'
                    }}</span
                    ><input
                      type="file"
                      accept=".txt,.csv,text/plain,text/csv"
                      class="sr-only"
                      @change="readFile" /></label
                  ><button
                    v-if="batchText"
                    class="text-sm font-medium text-n-slate-10 hover:text-n-ruby-10"
                    @click="
                      batchText = '';
                      selectedFileName = '';
                    "
                  >
                    Limpar lista
                  </button>
                </div>
              </div>
              <aside
                class="flex flex-col rounded-xl border border-n-weak bg-n-solid-2 p-5"
              >
                <p
                  class="text-xs font-semibold uppercase tracking-wide text-n-slate-10"
                >
                  Resumo do lote
                </p>
                <dl class="mt-4 grid gap-3 text-sm">
                  <div class="flex justify-between">
                    <dt class="text-n-slate-11">CPFs válidos e únicos</dt>
                    <dd class="font-semibold text-n-slate-12">
                      {{ batchCpfs.length }}
                    </dd>
                  </div>
                  <div class="flex justify-between">
                    <dt class="text-n-slate-11">Custo estimado</dt>
                    <dd class="font-semibold text-n-slate-12">
                      {{ batchCost }} créditos
                    </dd>
                  </div>
                  <div class="flex justify-between border-t border-n-weak pt-3">
                    <dt class="text-n-slate-11">Saldo após reserva</dt>
                    <dd
                      class="font-semibold"
                      :class="
                        balance - batchCost < 0
                          ? 'text-n-ruby-10'
                          : 'text-n-teal-10'
                      "
                    >
                      {{ (balance - batchCost).toLocaleString('pt-BR') }}
                    </dd>
                  </div>
                </dl>
                <Button
                  class="mt-auto w-full"
                  :label="processingBatch ? 'Criando lote…' : 'Processar lote'"
                  icon="i-lucide-play"
                  :is-loading="processingBatch"
                  :disabled="processingBatch || !canProcessBatch"
                  @click="sendBatch"
                />
                <Button
                  v-if="balance === 0 && isAdmin"
                  label="Comprar créditos"
                  icon="i-lucide-shopping-cart"
                  color="slate"
                  variant="outline"
                  class="mt-2 w-full"
                  @click="openCreditShop"
                />
              </aside>
            </div>
          </section>

          <section
            v-if="result"
            id="lemit-result"
            class="overflow-hidden rounded-2xl border border-n-teal-6 bg-n-solid-1 shadow-sm"
          >
            <div
              class="flex flex-wrap items-center justify-between gap-3 border-b border-n-weak bg-n-teal-2 px-5 py-4"
            >
              <div class="flex items-center gap-3">
                <span
                  class="grid size-10 place-items-center rounded-full bg-n-teal-3 text-n-teal-11"
                  ><i class="i-lucide-user-round-check size-5"
                /></span>
                <div>
                  <p
                    class="text-xs font-semibold uppercase tracking-wide text-n-teal-11"
                  >
                    Consulta concluída
                  </p>
                  <h2 class="text-lg font-semibold text-n-slate-12">
                    {{ person.nome || 'Dados localizados' }}
                  </h2>
                </div>
              </div>
              <div class="flex flex-wrap items-center gap-2">
                <span
                  v-if="result.contact_id"
                  class="rounded-full bg-n-teal-3 px-3 py-1 text-xs font-medium text-n-teal-11"
                  >Contato #{{ result.contact_id }} atualizado</span
                >
                <Button
                  label="Baixar PDF"
                  icon="i-lucide-file-down"
                  color="slate"
                  variant="outline"
                  size="sm"
                  @click="downloadPdf"
                />
              </div>
            </div>
            <div class="grid gap-5 p-5 md:grid-cols-2 xl:grid-cols-4">
              <div class="detail-card">
                <span>CPF</span><strong>{{ formatCpf(person.cpf) }}</strong>
              </div>
              <div class="detail-card">
                <span>Risco de crédito</span>
                <strong>
                  <span
                    class="inline-flex rounded-full px-2.5 py-1 text-xs font-semibold"
                    :class="creditRiskClass(creditRisk)"
                  >
                    {{ creditRiskScore(creditRisk) }}
                  </span>
                </strong>
              </div>
              <div class="detail-card">
                <span>Nascimento</span
                ><strong>{{ formatBirthDate(person.data_nascimento) }}</strong>
              </div>
              <div class="detail-card">
                <span>Nome da mãe</span
                ><strong>{{ person.nome_mae || '-' }}</strong>
              </div>
              <div class="detail-card">
                <span>Sexo</span><strong>{{ genderLabel(person.sexo) }}</strong>
              </div>
              <div class="detail-card">
                <span>Falecido</span
                ><strong
                  :class="person.falecido ? 'text-n-ruby-10' : 'text-n-teal-10'"
                  >{{ deceasedLabel(person.falecido) }}</strong
                >
              </div>
              <div class="detail-card">
                <span>Situação do CPF</span
                ><strong>{{ person.situacao_cpf || '-' }}</strong>
              </div>
              <div class="detail-card md:col-span-2 xl:col-span-2">
                <span>Ocupação</span
                ><strong>{{ person.ocupacao || '-' }}</strong>
              </div>

              <div class="detail-card md:col-span-2 xl:col-span-4">
                <div class="mb-3 flex items-center justify-between gap-3">
                  <span class="!mb-0">Celulares</span>
                  <small class="text-n-slate-10"
                    >{{ person.celulares?.length || 0 }} encontrado(s)</small
                  >
                </div>
                <div v-if="person.celulares?.length" class="grid gap-2">
                  <article
                    v-for="phone in person.celulares || []"
                    :key="`${phone.ddd}${phone.numero}`"
                    class="flex flex-wrap items-center gap-3 rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2.5"
                  >
                    <span
                      class="grid size-9 shrink-0 place-items-center rounded-full bg-n-teal-3 text-n-teal-11"
                      ><i class="i-lucide-smartphone size-4"
                    /></span>
                    <div class="min-w-40 flex-1">
                      <strong class="block text-sm text-n-slate-12">{{
                        formatPhone(phone)
                      }}</strong>
                      <div class="mt-1 flex flex-wrap gap-1.5">
                        <span
                          v-if="phone.whatsapp"
                          class="result-badge bg-n-teal-3 text-n-teal-11"
                          >WhatsApp</span
                        ><span
                          v-if="phone.plus"
                          class="result-badge bg-n-violet-3 text-n-violet-11"
                          >Plus</span
                        ><span
                          v-if="phone.ranking"
                          class="result-badge bg-n-alpha-2 text-n-slate-10"
                          >{{ phonePriorityLabel(phone.ranking) }}</span
                        >
                      </div>
                    </div>
                    <div class="flex items-center gap-2">
                      <Button
                        label="Copiar"
                        icon="i-lucide-copy"
                        color="slate"
                        variant="ghost"
                        size="sm"
                        @click="copyPhone(phone)"
                      />
                      <Button
                        :label="phoneActionLabel(phone)"
                        :icon="
                          phoneContact(phone).conversation_id
                            ? 'i-lucide-messages-square'
                            : phoneContact(phone).contact_id
                              ? 'i-lucide-contact'
                              : 'i-lucide-user-plus'
                        "
                        size="sm"
                        :is-loading="creatingPhoneKey === phoneKey(phone)"
                        :disabled="creatingPhoneKey === phoneKey(phone)"
                        @click="handlePhoneContact(phone)"
                      />
                    </div>
                  </article>
                </div>
                <strong v-else>-</strong>
              </div>

              <div class="detail-card md:col-span-2 xl:col-span-4">
                <div class="mb-3 flex items-center justify-between gap-3">
                  <span class="!mb-0">Endereços</span>
                  <small class="text-n-slate-10"
                    >{{ person.enderecos?.length || 0 }} encontrado(s)</small
                  >
                </div>
                <div
                  v-if="person.enderecos?.length"
                  class="grid gap-3 lg:grid-cols-3"
                >
                  <article
                    v-for="(address, index) in person.enderecos || []"
                    :key="index"
                    class="flex flex-col rounded-xl border border-n-weak bg-n-solid-1 p-4"
                  >
                    <div class="flex items-center justify-between gap-2">
                      <span
                        class="result-badge bg-n-blue-3 capitalize text-n-blue-11"
                        >{{ address.tipo || `Endereço ${index + 1}` }}</span
                      ><small v-if="address.ranking" class="text-n-slate-9">{{
                        addressPriorityLabel(address.ranking)
                      }}</small>
                    </div>
                    <strong
                      class="mt-3 block text-sm leading-5 text-n-slate-12"
                      >{{
                        address.logradouro || address.endereco || '-'
                      }}</strong
                    >
                    <p class="mt-1 text-sm leading-5 text-n-slate-11">
                      <template v-if="address.numero"
                        >Nº {{ address.numero }}</template
                      ><template v-if="address.complemento">
                        · {{ address.complemento }}</template
                      ><template v-if="address.bairro">
                        · {{ address.bairro }}</template
                      >
                    </p>
                    <p class="mt-2 text-sm text-n-slate-11">
                      {{
                        [address.cidade, address.uf].filter(Boolean).join(' — ')
                      }}
                    </p>
                    <span
                      v-if="address.cep"
                      class="mt-3 inline-flex items-center gap-1 font-mono text-xs text-n-slate-10"
                      ><i class="i-lucide-map-pin size-3.5" />CEP
                      {{ address.cep }}</span
                    >
                    <Button
                      label="Abrir no Google Maps"
                      icon="i-lucide-map"
                      color="slate"
                      variant="outline"
                      size="sm"
                      class="mt-4 self-start"
                      @click="openAddressOnMaps(address)"
                    />
                  </article>
                </div>
                <strong v-else>-</strong>
              </div>

              <div class="detail-card md:col-span-2 xl:col-span-4">
                <div class="mb-3 flex items-center justify-between gap-3">
                  <span class="!mb-0">Vínculos</span>
                  <small class="text-n-slate-10"
                    >{{ person.vinculos?.length || 0 }} encontrado(s)</small
                  >
                </div>
                <div
                  v-if="person.vinculos?.length"
                  class="grid gap-2 md:grid-cols-2"
                >
                  <article
                    v-for="(relation, index) in person.vinculos"
                    :key="`${relation.cpf_vinculo}-${index}`"
                    class="flex items-center gap-3 rounded-lg border border-n-weak bg-n-solid-1 p-3"
                  >
                    <span
                      class="grid size-9 shrink-0 place-items-center rounded-full bg-n-violet-3 text-n-violet-11"
                      ><i class="i-lucide-users-round size-4"
                    /></span>
                    <div class="min-w-0 flex-1">
                      <strong class="block truncate text-sm text-n-slate-12">{{
                        relation.nome_vinculo || '-'
                      }}</strong>
                      <small class="text-n-slate-10"
                        >{{ relation.tipo_vinculo || 'Vínculo' }} ·
                        {{ formatCpf(relation.cpf_vinculo) }}</small
                      >
                    </div>
                    <Button
                      label="Consultar CPF"
                      icon="i-lucide-search"
                      color="slate"
                      variant="outline"
                      size="xs"
                      :is-loading="
                        consultingCpf === normalizeCpf(relation.cpf_vinculo)
                      "
                      :disabled="
                        consulting ||
                        !providerAvailable ||
                        !hasBalance ||
                        !isValidCpf(relation.cpf_vinculo)
                      "
                      @click="requestRelatedCpfConsult(relation)"
                    />
                  </article>
                </div>
                <strong v-else>-</strong>
              </div>
            </div>
          </section>

          <section
            v-if="cachedConsultations.length"
            class="rounded-2xl border border-n-weak bg-n-solid-1 p-5 shadow-sm lg:p-6"
          >
            <div class="mb-4 flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 class="text-xl font-semibold text-n-slate-12">
                  Consultas recentes neste navegador
                </h2>
                <p class="mt-1 text-sm text-n-slate-11">
                  Abra novamente até 50 resultados sem consumir novos créditos.
                </p>
              </div>
              <Button
                label="Limpar histórico local"
                icon="i-lucide-trash-2"
                color="slate"
                variant="ghost"
                size="sm"
                @click="clearConsultationCache"
              />
            </div>
            <div class="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
              <button
                v-for="cached in cachedConsultations"
                :key="cached.id"
                class="flex items-center gap-3 rounded-xl border border-n-weak bg-n-solid-2 p-3 text-left transition hover:border-n-brand/50 hover:bg-n-alpha-1"
                @click="showCachedConsultation(cached)"
              >
                <span
                  class="grid size-10 shrink-0 place-items-center rounded-xl bg-n-blue-3 text-n-blue-11"
                  ><i class="i-lucide-history size-4"
                /></span>
                <span class="min-w-0 flex-1">
                  <strong class="block truncate text-sm text-n-slate-12">{{
                    cached.pessoa?.nome || formatCpf(cached.cpf)
                  }}</strong>
                  <small class="block text-n-slate-10"
                    >{{ formatCpf(cached.cpf) }} ·
                    {{ formatDate(cached.cached_at) }}</small
                  >
                  <span
                    v-if="cached.risco_credito?.score_credito"
                    class="mt-1 inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold"
                    :class="creditRiskClass(cached.risco_credito)"
                  >
                    Risco {{ creditRiskScore(cached.risco_credito) }}
                  </span>
                </span>
                <i class="i-lucide-chevron-right size-4 text-n-slate-9" />
              </button>
            </div>
          </section>

          <section
            v-if="batches.length"
            class="rounded-2xl border border-n-weak bg-n-solid-1 p-5 shadow-sm lg:p-6"
          >
            <div class="mb-5 flex items-center justify-between">
              <div>
                <h2 class="text-xl font-semibold text-n-slate-12">
                  Processamentos em lote
                </h2>
                <p class="mt-1 text-sm text-n-slate-11">
                  Acompanhe o progresso e baixe os resultados disponíveis em
                  CSV.
                </p>
              </div>
              <button
                class="btn btn-secondary"
                :disabled="loading"
                @click="load"
              >
                <i
                  class="i-lucide-refresh-cw size-4"
                  :class="{ 'animate-spin': loading }"
                />Atualizar
              </button>
            </div>
            <div class="grid gap-3">
              <article
                v-for="batch in batches"
                :key="batch.id"
                class="rounded-xl border border-n-weak bg-n-solid-2 p-4"
              >
                <div class="flex flex-wrap items-center justify-between gap-4">
                  <div class="min-w-0 flex-1">
                    <div class="flex flex-wrap items-center gap-2">
                      <strong class="text-sm text-n-slate-12"
                        >Lote {{ batch.id.slice(0, 8) }}</strong
                      ><span
                        class="rounded-full px-2 py-0.5 text-xs font-medium"
                        :class="
                          batch.complete
                            ? 'bg-n-teal-3 text-n-teal-11'
                            : 'bg-n-amber-3 text-n-amber-11'
                        "
                        >{{
                          batch.complete ? 'Concluído' : 'Processando'
                        }}</span
                      >
                    </div>
                    <p class="mt-1 text-xs text-n-slate-10">
                      {{ formatDate(batch.created_at) }} ·
                      {{ batch.agent || 'Agente' }}
                    </p>
                    <div
                      class="mt-3 h-1.5 overflow-hidden rounded-full bg-n-alpha-2"
                    >
                      <div
                        class="h-full rounded-full bg-n-brand transition-all"
                        :style="{ width: `${batchPercent(batch)}%` }"
                      />
                    </div>
                    <p class="mt-2 text-xs text-n-slate-10">
                      {{ batch.processed }} de {{ batch.total }} concluídos ·
                      <span class="text-n-teal-10"
                        >{{ batch.success }} sucesso</span
                      ><span v-if="batch.failed" class="text-n-ruby-10">
                        · {{ batch.failed }} falhas</span
                      >
                    </p>
                  </div>
                  <div class="flex flex-wrap items-center gap-2">
                    <Button
                      label="Gerar campanha"
                      icon="i-lucide-megaphone"
                      size="sm"
                      :disabled="!batch.complete || !batch.success"
                      @click="openCampaignDialog(batch)"
                    />
                    <Button
                      label="Baixar resultados"
                      icon="i-lucide-download"
                      color="slate"
                      variant="outline"
                      size="sm"
                      :is-loading="downloadingBatchId === batch.id"
                      :disabled="
                        !batch.download_ready || downloadingBatchId === batch.id
                      "
                      @click="downloadBatch(batch)"
                    />
                  </div>
                </div>
              </article>
            </div>
            <p class="mt-4 flex items-center gap-2 text-xs text-n-slate-10">
              <i class="i-lucide-info size-4" />O CSV inclui todos os campos
              retornados pela {{ providerName }} e até três registros de cada
              lista, como telefones, e-mails e endereços.
            </p>
          </section>

          <section
            v-if="isAdmin"
            class="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-n-weak bg-n-solid-1 p-5 shadow-sm lg:p-6"
          >
            <div class="flex items-center gap-3">
              <span
                class="grid size-10 place-items-center rounded-xl bg-n-violet-3 text-n-violet-11"
                ><i class="i-lucide-user-cog size-5"
              /></span>
              <div>
                <h2 class="text-base font-semibold text-n-slate-12">
                  Controle de acesso
                </h2>
                <p class="mt-0.5 text-sm text-n-slate-11">
                  {{ authorizedUsersCount }} de {{ users.length }} usuários
                  podem usar os produtos de higienização.
                </p>
              </div>
            </div>
            <Button
              label="Gerenciar usuários"
              icon="i-lucide-settings-2"
              color="slate"
              variant="outline"
              @click="openAccessDialog"
            />
          </section>

          <section
            class="rounded-2xl border border-n-weak bg-n-solid-1 shadow-sm"
          >
            <div class="grid gap-4 border-b border-n-weak p-5 lg:p-6">
              <div>
                <h2 class="text-xl font-semibold text-n-slate-12">
                  Histórico de consultas
                </h2>
                <p class="mt-1 text-sm text-n-slate-11">
                  Auditoria das consultas individuais e em lote.
                </p>
              </div>
              <div class="flex flex-col items-start gap-1.5">
                <label class="text-xs font-medium text-n-slate-11"
                  >Período</label
                >
                <WootDatePicker
                  v-model:date-range="dateRange"
                  v-model:range-type="rangeType"
                  @date-range-changed="onDateRangeChanged"
                />
              </div>
            </div>
            <div
              v-if="loading && !consultations.length"
              class="p-10 text-center text-sm text-n-slate-11"
            >
              <i
                class="i-lucide-loader-circle mx-auto mb-3 size-6 animate-spin text-n-brand"
              />Carregando histórico…
            </div>
            <div v-else-if="!consultations.length" class="p-12 text-center">
              <span
                class="i-lucide-file-search mx-auto mb-3 block size-8 text-n-slate-9"
              />
              <p class="font-medium text-n-slate-12">
                Nenhuma consulta encontrada
              </p>
              <p class="mt-1 text-sm text-n-slate-10">
                As consultas realizadas aparecerão aqui.
              </p>
            </div>
            <div v-else class="overflow-x-auto">
              <table class="w-full min-w-[950px] text-left">
                <thead
                  class="bg-n-solid-2 text-xs font-semibold uppercase tracking-wide text-n-slate-10"
                >
                  <tr>
                    <th class="px-5 py-3">Data e hora</th>
                    <th class="px-4 py-3">CPF</th>
                    <th class="px-4 py-3">Agente</th>
                    <th class="px-4 py-3">Origem</th>
                    <th class="px-4 py-3">Risco de crédito</th>
                    <th class="px-4 py-3">Status</th>
                    <th class="px-5 py-3 text-right">Contato</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-n-weak">
                  <tr
                    v-for="item in consultations"
                    :key="item.id"
                    class="transition hover:bg-n-alpha-1"
                  >
                    <td
                      class="whitespace-nowrap px-5 py-4 text-sm text-n-slate-11"
                    >
                      {{ formatDate(item.created_at) }}
                    </td>
                    <td
                      class="px-4 py-4 font-mono text-sm font-medium text-n-slate-12"
                    >
                      {{ formatCpf(item.cpf) }}
                    </td>
                    <td class="px-4 py-4 text-sm text-n-slate-11">
                      {{ item.agent }}
                    </td>
                    <td class="px-4 py-4">
                      <span
                        class="inline-flex items-center gap-1.5 text-sm text-n-slate-11"
                        ><i
                          :class="
                            item.source === 'batch'
                              ? 'i-lucide-layers-3'
                              : 'i-lucide-user-search'
                          "
                          class="size-4"
                        />{{ sourceLabel(item.source) }}</span
                      >
                    </td>
                    <td class="px-4 py-4">
                      <span
                        class="rounded-full px-2.5 py-1 text-xs font-semibold"
                        :class="creditRiskClass(item.risco_credito)"
                      >
                        {{ creditRiskScore(item.risco_credito) }}
                      </span>
                    </td>
                    <td class="px-4 py-4">
                      <span
                        class="rounded-full px-2.5 py-1 text-xs font-medium"
                        :class="statusClass(item.status)"
                        >{{ statusLabel(item.status) }}</span
                      ><small
                        v-if="item.error"
                        class="mt-1.5 block max-w-xs text-n-ruby-10"
                        >{{ item.error }}</small
                      >
                    </td>
                    <td class="px-5 py-4 text-right text-sm">
                      <span
                        v-if="item.contact_id"
                        class="rounded-md bg-n-teal-3 px-2 py-1 font-medium text-n-teal-11"
                        >#{{ item.contact_id }}</span
                      ><span v-else class="text-n-slate-9">—</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <Dialog
            ref="relatedConsultDialogRef"
            type="alert"
            title="Confirmar consulta do vínculo"
            :description="`Deseja consultar o CPF ${formatCpf(selectedRelation?.cpf_vinculo)}? Esta ação consumirá ${creditCost} crédito${creditCost === 1 ? '' : 's'} do saldo da conta.`"
            width="md"
            confirm-button-label="Consultar e usar crédito"
            cancel-button-label="Cancelar"
            :is-loading="consulting"
            :disable-confirm-button="!hasBalance || consulting"
            @confirm="confirmRelatedCpfConsult"
            @close="clearSelectedRelation"
          >
            <div
              class="flex items-start gap-3 rounded-xl border border-n-amber-7 bg-n-amber-3 p-3 text-sm text-n-amber-12"
            >
              <i class="i-lucide-coins mt-0.5 size-5 shrink-0" />
              <div>
                <strong class="block">Consulta com cobrança de crédito</strong>
                <span class="mt-1 block text-xs leading-5 text-n-amber-11">
                  Saldo atual: {{ balance.toLocaleString('pt-BR') }} créditos. O
                  resultado será salvo no histórico local.
                </span>
              </div>
            </div>
          </Dialog>

          <Dialog
            ref="campaignDialogRef"
            title="Gerar campanha do WhatsApp"
            description="Escolha quais telefones encontrados no lote serão usados. Você poderá revisar a lista, selecionar a caixa e o modelo antes de criar a campanha."
            width="lg"
            confirm-button-label="Continuar para campanha"
            cancel-button-label="Cancelar"
            :is-loading="preparingCampaign"
            @confirm="prepareBatchCampaign"
          >
            <div class="grid gap-2">
              <button
                v-for="option in campaignPhoneOptions"
                :key="option.value"
                type="button"
                class="flex items-start gap-3 rounded-xl border p-3 text-left transition"
                :class="
                  campaignPhonePriority === option.value
                    ? 'border-n-brand bg-n-brand/5'
                    : 'border-n-weak bg-n-solid-2 hover:border-n-strong'
                "
                @click="campaignPhonePriority = option.value"
              >
                <span
                  class="mt-0.5 grid size-5 shrink-0 place-items-center rounded-full border"
                  :class="
                    campaignPhonePriority === option.value
                      ? 'border-n-brand bg-n-brand text-white'
                      : 'border-n-strong'
                  "
                >
                  <i
                    v-if="campaignPhonePriority === option.value"
                    class="i-lucide-check size-3"
                  />
                </span>
                <span>
                  <strong class="block text-sm text-n-slate-12">{{
                    option.title
                  }}</strong>
                  <small class="mt-0.5 block leading-5 text-n-slate-10">{{
                    option.description
                  }}</small>
                </span>
              </button>
            </div>
          </Dialog>

          <Dialog
            ref="accessDialogRef"
            title="Usuários autorizados"
            description="Defina quais agentes podem realizar consultas. Administradores e super admins possuem acesso automático."
            width="2xl"
            overflow-y-auto
            confirm-button-label="Salvar acessos"
            cancel-button-label="Cancelar"
            :is-loading="savingAccesses"
            @confirm="saveAccesses"
          >
            <Input
              v-model="agentSearch"
              label="Localizar usuário"
              placeholder="Buscar por nome, e-mail ou perfil"
            />

            <div
              class="flex items-center justify-between text-xs text-n-slate-10"
            >
              <span>{{ filteredUsers.length }} usuário(s) encontrado(s)</span>
              <span>{{ draftAuthorizedUsersCount }} autorizado(s)</span>
            </div>

            <div class="grid max-h-[24rem] gap-2 overflow-y-auto pr-1">
              <label
                v-for="user in filteredUsers"
                :key="user.id"
                class="flex cursor-pointer items-center gap-3 rounded-lg border border-n-weak bg-n-solid-2 p-3 transition hover:border-n-brand/50"
              >
                <Checkbox
                  :model-value="userHasAccess(user)"
                  :disabled="user.automatic_access"
                  @update:model-value="updateUserAccess(user, $event)"
                />
                <span
                  class="grid size-9 shrink-0 place-items-center rounded-full bg-n-alpha-2 text-sm font-semibold text-n-slate-11"
                >
                  {{ user.name?.charAt(0)?.toUpperCase() }}
                </span>
                <span class="min-w-0 flex-1">
                  <strong class="block truncate text-sm text-n-slate-12">{{
                    user.name
                  }}</strong>
                  <small class="block truncate text-n-slate-10">{{
                    user.email
                  }}</small>
                </span>
                <span
                  class="rounded-full bg-n-alpha-2 px-2 py-1 text-xs text-n-slate-10"
                >
                  {{
                    user.role === 'administrator'
                      ? 'Administrador'
                      : user.automatic_access
                        ? 'Super admin'
                        : 'Agente'
                  }}
                </span>
              </label>

              <div
                v-if="!filteredUsers.length"
                class="rounded-lg border border-dashed border-n-weak p-8 text-center text-sm text-n-slate-10"
              >
                Nenhum usuário encontrado para “{{ agentSearch }}”.
              </div>
            </div>
          </Dialog>
        </div>
      </Transition>
    </div>
  </main>
</template>

<style scoped>
.provider-card {
  @apply relative min-h-[25rem] overflow-hidden rounded-2xl border border-n-weak bg-n-solid-2 p-6 text-left shadow-sm transition duration-300 ease-out hover:-translate-y-1 hover:bg-n-solid-1 hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-n-brand;
}
.provider-card__glow {
  @apply pointer-events-none absolute -right-16 -top-20 size-52 rounded-full opacity-0 blur-3xl transition duration-300;
}
.provider-card__icon {
  @apply grid size-14 place-items-center rounded-2xl transition duration-300;
}
.provider-card__arrow {
  @apply grid size-10 place-items-center rounded-full border border-n-weak bg-n-solid-1 text-n-slate-9 transition duration-300;
}
.provider-card:hover .provider-card__glow {
  @apply opacity-100;
}
.provider-card:hover .provider-card__icon {
  @apply scale-105;
}
.provider-card:hover .provider-card__arrow {
  @apply border-transparent text-white;
}
.provider-card__eyebrow {
  @apply inline-flex rounded-full px-2.5 py-1 text-[0.6875rem] font-semibold uppercase tracking-wide;
}
.provider-card--violet:hover {
  @apply border-n-violet-7;
}
.provider-card--violet .provider-card__glow {
  @apply bg-n-violet-7;
}
.provider-card--violet .provider-card__icon,
.provider-card__eyebrow--violet {
  @apply bg-n-violet-3 text-n-violet-11;
}
.provider-card--violet:hover .provider-card__arrow {
  @apply bg-n-violet-9;
}
.provider-card--blue:hover {
  @apply border-n-blue-7;
}
.provider-card--blue .provider-card__glow {
  @apply bg-n-blue-7;
}
.provider-card--blue .provider-card__icon,
.provider-card__eyebrow--blue {
  @apply bg-n-blue-3 text-n-blue-11;
}
.provider-card--blue:hover .provider-card__arrow {
  @apply bg-n-blue-9;
}
.hygiene-view-enter-active,
.hygiene-view-leave-active {
  transition:
    opacity 180ms ease,
    transform 220ms ease;
}
.hygiene-view-enter-from {
  opacity: 0;
  transform: translateY(12px) scale(0.992);
}
.hygiene-view-leave-to {
  opacity: 0;
  transform: translateY(-8px) scale(0.996);
}
.metric-card {
  @apply flex min-h-24 items-center gap-3 rounded-xl border border-n-weak bg-n-solid-1 p-4 shadow-sm;
}
.metric-card p {
  @apply text-xs font-medium text-n-slate-10;
}
.metric-card strong {
  @apply mt-1 block text-base font-semibold text-n-slate-12;
}
.metric-icon {
  @apply grid size-11 shrink-0 place-items-center rounded-xl;
}
.mode-tab {
  @apply inline-flex flex-1 items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium text-n-slate-10 transition hover:text-n-slate-12;
}
.mode-tab--active {
  @apply bg-n-solid-1 text-n-slate-12 shadow-sm;
}
.detail-card {
  @apply rounded-xl border border-n-weak bg-n-solid-2 p-4;
}
.detail-card > span {
  @apply mb-1.5 block text-xs font-medium uppercase tracking-wide text-n-slate-10;
}
.detail-card > strong {
  @apply text-sm font-semibold leading-6 text-n-slate-12;
}
.result-badge {
  @apply inline-flex items-center rounded-full px-2 py-0.5 text-[0.6875rem] font-medium;
}
</style>
